import { FC, useCallback } from 'react';
import { DatePicker, Input, InputSelect, Switch, Textarea } from '@features/ui';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
} from 'react-hook-form';
import {
  ProductAccountTypes,
  ProductFormData,
  ProductTradingAccountTypes,
} from '@src/types';
import {
  productAccountTypeData,
  productTradingAccountTypeData,
} from '@features/admin/productsAddons/productsAddonsData';
import { ProductCompatibleDataFeeds } from '@features/admin/productsAddons/ProductCompatibleDataFeeds.tsx';
import { cn } from '@lib/utils.ts';

export const ProductTradingAccount: FC<UseFormReturn<ProductFormData>> = ({
  control,
  formState: { errors },
  resetField,
  watch,
}) => {
  const isBundle = watch('type') === ProductAccountTypes.BUNDLE;
  const isWeekly = watch('type') === ProductAccountTypes.WEEKLY;
  const accountType = watch('account_type');
  const trailingDrawdownStops = watch('trailing_drawdown_stops');

  const showPerformanceAccount =
    accountType === ProductTradingAccountTypes.PRACTICE_ACCOUNT ||
    accountType === ProductTradingAccountTypes.COMPETITION;

  const showActivationDate =
    accountType === ProductTradingAccountTypes.SPECIALTY_ACCOUNT;

  const handleChangeAccountType = useCallback(
    (
      field: ControllerRenderProps<ProductFormData, 'account_type'>,
      value: string
    ) => {
      field.onChange(value);
      if (
        accountType === ProductTradingAccountTypes.DEMO_ACCOUNT ||
        accountType === ProductTradingAccountTypes.STRAIGHT_TO_PA
      ) {
        resetField('is_performance_account');
      }
    },
    [accountType, resetField]
  );

  return (
    <>
      <div
        className={
          'flex lg:flex-row flex-col xl:w-[70%] w-full lg:mt-[49px] mt-7 gap-x-[59px] gap-y-7'
        }
      >
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-2'}>
              <p>Account type</p>
              <InputSelect
                className={'text-xs'}
                error={!!errors?.type?.message}
                options={productAccountTypeData}
                placeholder={'Select'}
                value={field.value ?? ''}
                onChange={(value) => {
                  field.onChange(value);
                  resetField('weekly_account_payout_max');
                  resetField('accounts_in_bundle');
                }}
              />
            </div>
          )}
        />
        {isBundle ? (
          <Controller
            control={control}
            name="accounts_in_bundle"
            render={({ field }) => (
              <div className={cn('flex flex-col gap-2 flex-1')}>
                <p className={'ml-2'}>How many accounts in a bundle</p>
                <Input
                  {...field}
                  placeholder={'Enter'}
                  textSize={'xs'}
                  type={'number'}
                  value={field.value ?? ''}
                  variant={'secondary'}
                />
              </div>
            )}
          />
        ) : null}
        {isWeekly ? (
          <Controller
            control={control}
            name="weekly_account_payout_max"
            render={({ field }) => (
              <div className={cn('flex flex-col gap-2 flex-1')}>
                <p className={'ml-2'}>Max Account Payout $</p>
                <Input
                  {...field}
                  placeholder={'Enter'}
                  textSize={'xs'}
                  type={'number'}
                  value={field.value ?? ''}
                  variant={'secondary'}
                />
              </div>
            )}
          />
        ) : null}
        {!isBundle && !isWeekly ? <div className={'flex-1'} /> : null}
      </div>
      <div
        className={
          'flex lg:flex-row flex-col xl:w-[70%] w-full lg:mt-[49px] mt-7 gap-x-[59px] gap-y-7'
        }
      >
        <Controller
          control={control}
          name="account_benefits"
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-2'}>
              <p>Account benefits (to display during checkout process)</p>
              <Textarea
                {...field}
                hiddenHeader
                className={'max-h-42'}
                id={field.name}
                placeholder={'Enter'}
                value={field.value ?? ''}
              />
            </div>
          )}
        />
        <div className={'lg:flex hidden flex-1'} />
      </div>
      <div
        className={
          'flex lg:flex-row flex-col xl:w-[70%] w-full lg:mt-[49px] mt-7 gap-x-[59px] gap-y-7'
        }
      >
        <Controller
          control={control}
          name="account_type"
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-2'}>
              <p>What type of account is this?</p>
              <InputSelect
                className={'text-xs'}
                error={!!errors?.account_type?.message}
                options={productTradingAccountTypeData}
                placeholder={'Select'}
                value={field.value ?? ''}
                onChange={(value) => handleChangeAccountType(field, value)}
              />
            </div>
          )}
        />
        <div className={'flex-1'}>
          <Controller
            control={control}
            name="is_performance_account"
            render={({ field }) => (
              <div
                className={cn(
                  showPerformanceAccount ? 'flex' : 'hidden',
                  'flex-col gap-2'
                )}
              >
                <div className={'h-[18px] lg:flex hidden'} />
                <Switch
                  checked={!!field.value}
                  className={'mt-2'}
                  label={'Can this be a performance Account'}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </div>
            )}
          />
          <Controller
            control={control}
            name="competition_activation_date"
            render={({ field }) => (
              <div
                className={cn(
                  showActivationDate ? 'flex' : 'hidden',
                  'flex-col gap-2'
                )}
              >
                <p>Activation Date</p>
                <DatePicker
                  value={field.value}
                  onChange={(date) =>
                    field.onChange(date ? date.toISOString() : null)
                  }
                />
              </div>
            )}
          />
        </div>
      </div>
      <div
        className={
          'flex lg:flex-row flex-col xl:w-[70%] w-full lg:mt-[61px] mt-7 gap-x-[59px] gap-y-7'
        }
      >
        <ProductCompatibleDataFeeds control={control} errors={errors} />
        <div className={'flex-1 lg:flex hidden'} />
      </div>
      <div className={'w-fit flex flex-col gap-[5px] mt-[57px]'}>
        <p className={'text-[16px]'}>Financials</p>
        <div className={'w-full h-[1px] bg-white'} />
      </div>
      <div
        className={
          'mt-[25px] flex lg:flex-row flex-col xl:w-[80%] w-full gap-7'
        }
      >
        <Controller
          control={control}
          name="cash_in_hand"
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-[14px]'}>
              <p>Cash In Hand $</p>
              <Input
                {...field}
                error={!!errors.cash_in_hand?.message}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="auto_liquidate"
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-[14px]'}>
              <p>Auto Liquidate $</p>
              <Input
                {...field}
                error={!!errors.auto_liquidate?.message}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="drawdown"
          render={({ field }) => (
            <div className={'flex flex-1 flex-col gap-[14px]'}>
              <p>Drawdown</p>
              <Input
                {...field}
                error={!!errors.drawdown?.message}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
            </div>
          )}
        />
      </div>
      <div
        className={
          'flex lg:flex-row flex-col w-full lg:mt-[71px] mt-7 2xl:gap-x-[71px] gap-x-7 gap-y-7 lg:items-end'
        }
      >
        <Controller
          control={control}
          name="trailing_drawdown_stop_at"
          render={({ field }) => (
            <div
              className={cn(
                'flex-col gap-[14px]',
                trailingDrawdownStops ? 'flex' : 'hidden'
              )}
            >
              <p>Practice Account Trailing Drawdown Stop At</p>
              <Input
                {...field}
                error={!!errors.trailing_drawdown_stop_at?.message}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
            </div>
          )}
        />
        <Controller
          control={control}
          name="pa_account_trailing_drawdown_stop_at"
          render={({ field }) => (
            <div
              className={cn(
                'flex-col gap-[14px]',
                trailingDrawdownStops ? 'flex' : 'hidden'
              )}
            >
              <p>Performance Account Trailing Drawdown Stop At</p>
              <Input
                {...field}
                error={!!errors.pa_account_trailing_drawdown_stop_at?.message}
                placeholder={'0'}
                textSize={'xs'}
                type={'number'}
                value={field.value ?? ''}
                variant={'secondary'}
              />
            </div>
          )}
        />
      </div>
    </>
  );
};
